// src/components/admin/GodModeSwitcher.js
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, query, getDocs, doc, updateDoc } from 'firebase/firestore';
import { Shield, ChevronRight, Check } from 'lucide-react';

const GodModeSwitcher = ({ currentUserProfile, onClose }) => {
    // 1. HOOKS FIRST (Always at the top)
    const [orgs, setOrgs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [switchingTo, setSwitchingTo] = useState(null);

    useEffect(() => {
        const fetchOrgs = async () => {
            try {
                const q = query(collection(db, 'organizations'));
                const snap = await getDocs(q);
                const orgList = snap.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                }));
                setOrgs(orgList);
            } catch (error) {
                console.error("Failed to fetch organizations:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchOrgs();
    }, []);

    // 2. SECURITY CHECK (After hooks, before render)
    // If they aren't an admin, show nothing.
    // Note: You can also use your custom isSupremeLeader check here if you pass it down, 
    // but checking the role is a good UI-level fallback.
    const isAllowed = 
        currentUserProfile.role === 'Owner/System Admin' || 
        currentUserProfile.role === 'System Admin' || 
        currentUserProfile.role === 'Admin'; // Added Admin for safety

    if (!isAllowed) {
        return null;
    }

    const handleSwitch = async (orgId, orgName) => {
        if (!window.confirm(`Are you sure you want to impersonate: ${orgName}?`)) return;
        
        setSwitchingTo(orgId);
        try {
            const userRef = doc(db, 'users', currentUserProfile.id);
            await updateDoc(userRef, {
                organizationId: orgId,
                organizationName: orgName 
            });
            
            if (onClose) onClose();
        } catch (error) {
            console.error("God Mode Switch Failed:", error);
            alert("Failed to switch: " + error.message);
        } finally {
            setSwitchingTo(null);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-md rounded-xl shadow-2xl border-2 border-purple-600 overflow-hidden">
                <div className="bg-purple-900 px-6 py-4 flex justify-between items-center">
                    <h2 className="text-white font-bold text-lg flex items-center gap-2">
                        <Shield className="text-yellow-400" fill="currentColor" /> 
                        GOD MODE ACCESS
                    </h2>
                    <button onClick={onClose} className="text-purple-200 hover:text-white">Close</button>
                </div>
                
                <div className="p-4 max-h-[60vh] overflow-y-auto">
                    {loading ? (
                        <p className="text-center py-4">Scanning multiverse...</p>
                    ) : (
                        <div className="space-y-2">
                            {orgs.map(org => {
                                const isActive = currentUserProfile.organizationId === org.id;
                                return (
                                    <button 
                                        key={org.id}
                                        onClick={() => !isActive && handleSwitch(org.id, org.name || 'Unnamed Org')}
                                        disabled={isActive || switchingTo}
                                        className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                                            isActive 
                                            ? 'bg-green-50 border-green-500 ring-1 ring-green-500' 
                                            : 'hover:bg-gray-50 border-gray-200'
                                        }`}
                                    >
                                        <div className="text-left">
                                            <p className={`font-bold ${isActive ? 'text-green-800' : 'text-gray-800'}`}>
                                                {org.name || 'Unnamed Organization'}
                                            </p>
                                            <p className="text-xs text-gray-400 font-mono">{org.id}</p>
                                        </div>
                                        <div>
                                            {switchingTo === org.id ? (
                                                <span className="text-xs text-purple-600 font-bold animate-pulse">SWITCHING...</span>
                                            ) : isActive ? (
                                                <div className="flex items-center gap-1 text-green-600 font-bold text-xs bg-white px-2 py-1 rounded-full border border-green-200 shadow-sm">
                                                    <Check size={12} /> ACTIVE
                                                </div>
                                            ) : (
                                                <ChevronRight className="text-gray-300" />
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="bg-gray-50 p-3 text-center border-t">
                    <p className="text-xs text-gray-500">
                        Warning: Any changes made will be live in the customer's database.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default GodModeSwitcher;